/**
 * Fetch scorecard templates for the template selector in ScorecardStep.
 * GET /api_ats/v1/ats_scorecards/list_templates
 * Supports optional job-context filtering by department/office/employment_type.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import qs from 'qs';
import { http } from '@/lib/ats-https';
import { asAtsClientBody } from '@/api/ats/atsEnvelope';
import { getAtsJobCreate } from '@/store/atsJobCreate';

export interface ScorecardTemplate {
  name: string;
  questions?: Array<{
    type?: string;
    question?: string;
    description?: string;
    options?: string[];
  }>;
  feedback_description?: string;
  is_feedback_mandatory?: boolean;
  rating_description?: string;
}

interface SelectOption {
  value: string;
  label: string;
}

async function fetchScorecardTemplates(
  params?: string
): Promise<ScorecardTemplate[]> {
  const url = params
    ? `ats_scorecards/list_templates?${params}`
    : 'ats_scorecards/list_templates';
  const raw = asAtsClientBody<{
    data?: { ats_scorecards?: ScorecardTemplate[] };
    ats_scorecards?: ScorecardTemplate[];
  }>(await http.get(url));
  return raw?.data?.ats_scorecards ?? raw?.ats_scorecards ?? [];
}

export function useScorecardTemplates() {
  // Read job context for filtering templates by department/office/employment_type
  const { currentJob } = useSelector(getAtsJobCreate) as {
    currentJob?: {
      data?: {
        basic_info?: {
          department?: { id?: number };
          office?: Array<{ id?: number }>;
          employment_type?: string;
        };
      };
    };
  };

  const queryParams = useMemo(() => {
    const basicInfo = currentJob?.data?.basic_info;
    if (!basicInfo) return undefined;

    const params: Record<string, unknown> = {};
    if (basicInfo.department?.id) {
      params.department_ids = [basicInfo.department.id];
    }
    if (basicInfo.office?.length) {
      params.office_ids = basicInfo.office.map((o) => o.id);
    }
    if (basicInfo.employment_type) {
      params.employment_types = [basicInfo.employment_type];
    }
    if (Object.keys(params).length === 0) return undefined;
    return qs.stringify(params, { arrayFormat: 'brackets' });
  }, [currentJob]);

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['scorecard-templates', queryParams],
    queryFn: () => fetchScorecardTemplates(queryParams),
    staleTime: 5 * 60 * 1000,
  });

  const templateOptions = useMemo(
    (): SelectOption[] =>
      templates.map((t) => ({
        value: t.name,
        label: t.name,
      })),
    [templates]
  );

  return { templates, templateOptions, isLoading };
}
